import type { StatementDto, StatementReportMeta } from "@hisense/shared";
import monoFont from "@fontsource/ibm-plex-mono/files/ibm-plex-mono-latin-400-normal.woff2?url";
import { printHtml } from "./printWindow";
import {
  STATEMENT_BUCKET_ORDER,
  statementLineState,
  statementMoney,
} from "./statementSheets";

const esc = (s: string) =>
  s.replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!,
  );
const name = (value: string | null | undefined) =>
  esc(value?.trim() || "Unassigned customer");
const formatReportDate = (
  ms: number | null | undefined,
  offsetMinutes: number,
) =>
  ms == null
    ? "—"
    : new Date(ms - offsetMinutes * 60_000).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
const moneyCell = (value: number) =>
  `<td class="r mono">${statementMoney(value)}</td>`;
const stateLabels = {
  archived: "Archived (scope removed)",
  settled: "Settled",
  credit: "Credit",
  review: "Review",
  overdue: "Overdue",
  current: "Current",
};
const filterLabels = {
  all: "All balances",
  outstanding: "Outstanding receivables",
  overdue: "Overdue receivables",
  settled: "Settled",
  credit: "Credit balances",
  review: "Needs review",
};
const dueReasons: Record<string, string> = {
  missingTerms: "Payment terms missing",
  unknownTerms: "Payment terms not recognized",
  missingDelivery: "Delivery date missing",
  statementTermsReview: "Statement-based terms require review",
};
const basisWarning =
  "Current PI values and terms are used; prior header edits are not reconstructed. Invoices are reference only and do not create an additional receivable.";

/** A deterministic document built from the displayed server response, including its cutoff and scope. */
export function buildStatementHtml(
  groups: StatementDto[],
  meta: StatementReportMeta,
): string {
  const printDate = (value: number | null | undefined) =>
    formatReportDate(value, meta.tzOffsetMinutes);
  const offset = -meta.tzOffsetMinutes;
  const zone = `UTC${offset >= 0 ? "+" : "-"}${String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0")}:${String(Math.abs(offset) % 60).padStart(2, "0")}`;
  const moment = (ms: number) =>
    new Date(ms - meta.tzOffsetMinutes * 60000).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      timeZone: "UTC",
    }) + ` ${zone}`;
  const customerStatement = Boolean(meta.customerId);
  const title = customerStatement
    ? "Statement of account"
    : "Internal receivables report";
  type Totals = Pick<
    StatementDto,
    | "openingBalance"
    | "periodBilled"
    | "periodPaid"
    | "periodAdjustments"
    | "totalBalance"
    | "receivable"
    | "credit"
    | "overdueAmount"
    | "overdueCount"
    | "unagedAmount"
  >;
  const byCurrency = new Map<string, Totals>();
  for (const g of groups) {
    const sum = byCurrency.get(g.currency) ?? {
      openingBalance: 0,
      periodBilled: 0,
      periodPaid: 0,
      periodAdjustments: 0,
      totalBalance: 0,
      receivable: 0,
      credit: 0,
      overdueAmount: 0,
      overdueCount: 0,
      unagedAmount: 0,
    };
    for (const key of Object.keys(sum) as Array<keyof Totals>)
      sum[key] += g[key];
    byCurrency.set(g.currency, sum);
  }
  const summary = (sum: Totals) =>
    [
      ["Opening balance", sum.openingBalance],
      ["Period billed", sum.periodBilled],
      ["Net receipts", sum.periodPaid],
      ["Scope adjustments", sum.periodAdjustments],
      ["Closing balance", sum.totalBalance],
      ["Receivable", sum.receivable],
      ["Credit", sum.credit],
      [`Overdue (${sum.overdueCount})`, sum.overdueAmount],
      ["Unaged receivable", sum.unagedAmount],
    ]
      .map(
        ([label, value]) =>
          `<div class="summary-row"><span>${label}</span><strong class="mono">${statementMoney(value as number)}</strong></div>`,
      )
      .join("");
  const sections = groups
    .map((g) => {
      const rows = g.lines
        .map((line) => {
          const state = statementLineState(line);
          const due =
            line.dueBasis === "unknown" || line.dueAt == null
              ? `Unknown${line.dueReason ? `<br><span class="muted">${esc(dueReasons[line.dueReason] ?? line.dueReason)}</span>` : ""}`
              : `${printDate(line.dueAt)}<br><span class="muted">PI delivery + terms (legacy basis)${line.dueReason ? `<br>${esc(dueReasons[line.dueReason] ?? line.dueReason)}` : ""}</span>`;
          const aging =
            line.balance <= 0
              ? "—"
              : state === "review"
                ? "Unknown"
                : esc(line.bucket);
          return `<tr>
        <td><span class="mono b">${esc(line.piNo)}</span><br><span class="muted">${printDate(line.date)}</span>${line.archived ? '<br><span class="muted">Archived PI</span>' : ""}${line.historyUncertain ? '<br><span class="muted">History requires review</span>' : ""}</td>
        <td class="mono">${line.invoiceNo ? esc(line.invoiceNo) : "—"}${line.invoiceArchived ? '<br><span class="muted">Archived invoice</span>' : ""}</td>
        ${moneyCell(line.openingBalance)}${moneyCell(line.periodBilled)}${moneyCell(line.periodPaid)}${moneyCell(line.periodAdjustments)}${moneyCell(line.balance)}
        <td>${due}</td><td${state === "overdue" ? ' class="overdue"' : ""}>${stateLabels[state]}<br><span class="muted">${aging}${state === "overdue" ? ` · ${line.overdueDays} d` : ""}</span></td>
      </tr>`;
        })
        .join("");
      const aging = STATEMENT_BUCKET_ORDER.map((bucket) => {
        const amount =
          g.buckets.find((entry) => entry.bucket === bucket)?.amount ?? 0;
        return `<td><div class="muted">${esc(bucket)}</div><strong class="mono">${statementMoney(amount)}</strong></td>`;
      }).join("");
      return `<section class="group"><div class="group-heading"><h2>${name(g.customerName)}</h2><strong class="mono">${esc(g.currency)} · ${statementMoney(g.totalBalance)}</strong></div>
      <div class="group-summary">${summary(g)}</div>
      <table class="aging"><caption>Aging of positive receivables · ${esc(g.currency)}</caption><tbody><tr>${aging}</tr></tbody></table>
      <table class="details"><colgroup><col style="width:16%"><col style="width:12%"><col span="5" style="width:9%"><col style="width:14%"><col style="width:13%"></colgroup>
        <thead><tr><th>PI / date</th><th>Invoice ref.</th><th class="r">Opening</th><th class="r">Billed</th><th class="r">Net receipts</th><th class="r">Adjustments</th><th class="r">Closing</th><th>Due date</th><th>Status / aging</th></tr></thead>
        <tbody>${rows}</tbody><tfoot><tr><td colspan="2">Subtotal · ${esc(g.currency)}</td>${moneyCell(g.openingBalance)}${moneyCell(g.periodBilled)}${moneyCell(g.periodPaid)}${moneyCell(g.periodAdjustments)}${moneyCell(g.totalBalance)}<td colspan="2"></td></tr></tfoot>
      </table>${g.historyLimited ? '<p class="muted">Recorded payment history is incomplete or uncertain for this customer/currency scope.</p>' : ""}</section>`;
    })
    .join("");
  const currencyTotals = [...byCurrency]
    .map(
      ([currency, total]) =>
        `<div class="currency-total"><h2>${esc(currency)} · Scope totals</h2>${summary(total)}</div>`,
    )
    .join("");
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title><style>
    @font-face { font-family: "IBM Plex Mono"; src: url("${esc(monoFont)}") format("woff2"); font-weight:400; font-style:normal; }
    @page { size:A4 landscape; margin:12mm; }
    * { box-sizing:border-box; } body { margin:0; color:black; font-family:Arial,sans-serif; font-size:10px; line-height:1.4; }
    .mono { font-family:"IBM Plex Mono",monospace; font-variant-numeric:tabular-nums; } .r { text-align:right; } .b { font-weight:bold; } .muted { color:dimgray; font-size:9px; } .overdue { font-weight:bold; }
    header,.group-heading { display:flex; align-items:baseline; justify-content:space-between; gap:16px; } h1 { font-size:23px; margin:0; } h2 { font-size:13px; margin:0; } .scope { border:1px solid gray; padding:10px; margin:12px 0; } .scope dl { display:grid; grid-template-columns:95px 1fr 90px 1fr; margin:6px 0 0; gap:3px 10px; } dt { color:dimgray; } dd { margin:0; }
    .notice { padding:7px 10px; border-left:3px solid black; background:whitesmoke; } .group { margin-top:22px; } .group-heading { border-bottom:2px solid black; padding-bottom:5px; break-after:avoid; }
    .group-summary { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:3px 24px; padding:8px 0; break-inside:avoid; } .summary-row { display:flex; justify-content:space-between; gap:12px; }
    table { width:100%; border-collapse:collapse; margin-top:8px; table-layout:fixed; } caption { text-align:left; font-weight:bold; padding-bottom:4px; } th { text-align:left; background:whitesmoke; font-size:9px; } th,td { border:1px solid silver; padding:5px; vertical-align:top; overflow-wrap:anywhere; } .aging td { text-align:center; } .aging { break-inside:avoid; } thead { display:table-header-group; } tfoot { display:table-row-group; font-weight:bold; } tr { break-inside:avoid; } .totals { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; margin-top:20px; } .currency-total { border-top:2px solid black; padding-top:6px; break-inside:avoid; } .currency-total h2 { margin-bottom:5px; } footer { margin-top:20px; border-top:1px solid silver; padding-top:6px; font-size:9px; color:dimgray; }
  </style></head><body data-generated-at="${esc(new Date(meta.generatedAt).toISOString())}" data-asof="${esc(new Date(meta.asOf).toISOString())}">
    <header><div><img src="/brand/hisense-logo.webp" alt="Hisense Button" style="height:32px;display:block;margin-bottom:5px"/><strong>HISENSE BUTTON CO., LTD.</strong><div class="muted">Customer receivables · PI basis</div></div><h1>${esc(title)}</h1></header>
    <div class="scope"><strong>${customerStatement ? `Statement for ${name(groups[0]?.customerName)}` : "Internal use · All customers in the selected scope"}</strong><dl>
      <dt>As at</dt><dd>${esc(moment(meta.asOf))}</dd>
      <dt>Period start</dt><dd>${meta.from == null ? "All available recorded history" : printDate(meta.from)}</dd>
      <dt>Report mode</dt><dd>${meta.mode === "asOf" ? "Historical as-of snapshot" : "Current snapshot"}</dd>
      <dt>Balance filter</dt><dd>${filterLabels[meta.filter]}</dd>
      <dt>Search</dt><dd>${meta.q ? esc(meta.q) : "All matching documents"}</dd>
      <dt>Sort</dt><dd>${{ date: "PI date, oldest first", "-date": "PI date, newest first", due: "Due date, earliest first", "-balance": "Closing balance, highest first" }[meta.sort ?? "-date"]}</dd>
      <dt>Currency scope</dt><dd>${meta.currency ? esc(meta.currency) : "All currencies, shown separately"}</dd>
      <dt>Generated</dt><dd>${esc(moment(meta.generatedAt))}</dd></dl></div>
    <p class="notice">${basisWarning}${meta.historyLimited ? " Recorded payment history is incomplete or uncertain; opening balances must be reviewed against source records." : " Balances use available recorded payment history."}</p>
    <p class="muted">Each PI total and its aggregated receipts are rounded to two decimals for reporting; stored amounts are unchanged.</p>
    <p class="muted">Opening balance + period billed − net receipts + scope adjustments = closing balance. Scope adjustments reflect PI archive scope changes, not credit notes. Net receipts include payment voids and may be negative. Opening balances use the same selected document scope. Aging excludes settled and credit balances; unknown due dates require review.</p>
    ${sections || "<p>No documents match this report scope.</p>"}
    <div class="totals">${currencyTotals}</div>
    <footer>Hisense Button · hisensebutton.toankhontech.com · Snapshot generated ${esc(moment(meta.generatedAt))}. Please review this statement and advise us of any differences.</footer>
  </body></html>`;
}

export function printStatement(
  groups: StatementDto[],
  meta: StatementReportMeta,
): void {
  printHtml(buildStatementHtml(groups, meta));
}
